import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement, R1FactType } from '@/api/rest/resources/types/fact';
import { DefaultWidgetNames, WidgetType } from '@/api/rest/resources/types/widget';
import { useToggle } from '@/hooks/useToggle';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';

const MAX_PREVIEW_INDICATORS = 5;

export const useResilienceDriversCard = () => {
  const { t } = useTranslation();
  const [isIndicatorDialogOpen, toggleIsIndicatorDialogOpen] = useToggle(false);
  const [isShowAllDialogOpen, toggleIsShowAllDialogOpen] = useToggle(false);
  const [selectedIndicator, setSelectedIndicator] = useState<DefaultWidgetNames | DefaultWidgetNames[] | null>(null);

  const factList = {
    whcFactKeys: {
      currentFact: R1FactType.r1_water_holding_capacity_latest_analysis_per_ha,
      maxFact: R1FactType.water_fc_per_ha_max,
      trendFact: R1FactType.r1_water_whc_increase_previous_1y_uplift_per_ha_pct_change,
    },
    carbonFactKeys: {
      currentFact: R1FactType.carbon_combined_per_ha,
      trendFact: R1FactType.carbon_combined_previous_1y_pct_change,
      maxFact: R1FactType.carbon_combined_max,
    },
    pofhFactKeys: {
      currentFact: R1FactType.r1_biodiversity_latest_analysis,
      trendFact: R1FactType.r1_biodiversity_buffer_zone_pct_previous_1y_uplift_change,
      maxFact: R1FactType.biodiversity_buffer_zone_pct_max,
    },
    bgcFactKeys: {
      currentFact: R1FactType.r1_carbon_bg_latest_analysis_per_ha,
      maxFact: R1FactType.permanent_soil_carbon_per_ha_max,
      trendFact: R1FactType.r1_carbon_bg_previous_1y_uplift_per_ha_pct_change,
    },
    socialFactKeys: {
      currentFact: R1FactType.social_life_expectancy,
      trendFact: R1FactType.social_life_expectancy_previous_1y_pct_change,
      maxFact: R1FactType.social_life_expectancy_max,
    },
  };

  const facts = useOrganizationIndicatorReportingFact(Object.values(factList).flatMap(Object.values));

  const whcFactValues = {
    ...Object.fromEntries(Object.entries(factList.whcFactKeys).map(([key, factType]) => [key, facts[`${factType}`]])),
  } as { [k: string]: PlotReportFactElement<unknown> | undefined };
  const carbonFactValues = Object.fromEntries(
    Object.entries(factList.carbonFactKeys).map(([key, factType]) => [key, facts[`${factType}`]]),
  );
  const pofhFactValues = Object.fromEntries(
    Object.entries(factList.pofhFactKeys).map(([key, factType]) => [key, facts[`${factType}`]]),
  );
  const bgcFactValues = {
    ...Object.fromEntries(Object.entries(factList.bgcFactKeys).map(([key, factType]) => [key, facts[`${factType}`]])),
  } as { [k: string]: PlotReportFactElement<unknown> | undefined };
  const socialFactValues = Object.fromEntries(
    Object.entries(factList.socialFactKeys).map(([key, factType]) => [key, facts[`${factType}`]]),
  );

  const handleResilienceDriverClick = (indicator: DefaultWidgetNames | DefaultWidgetNames[]) => {
    toggleIsIndicatorDialogOpen();
    setSelectedIndicator(Array.isArray(indicator) ? indicator : [indicator]);
  };

  const hasNoContent =
    Object.keys(facts).length === 0 ||
    [whcFactValues, carbonFactValues, pofhFactValues, bgcFactValues, socialFactValues].every(
      ({ currentFact, trendFact, maxFact }) => {
        const willProgressBarBeDisplayed = !isNullish(maxFact) && !isNullish(currentFact);
        const willTrendBeDisplayed = !isNullish(trendFact);

        return !willProgressBarBeDisplayed && !willTrendBeDisplayed;
      },
    );

  const indicators = [
    useIndicatorSection({
      handleClick: () => handleResilienceDriverClick(DefaultWidgetNames.default_project_whc_indicator_card),
      values: whcFactValues,
      indicator: 'waterHoldingCapacity',
    }),
    useIndicatorSection({
      handleClick: () =>
        handleResilienceDriverClick([
          DefaultWidgetNames.default_project_agc_indicator_card,
          DefaultWidgetNames.default_project_bgc_indicator_card,
        ]),
      values: carbonFactValues,
      indicator: 'combinedCarbon',
    }),
    usePofhIndicatorSection({
      handleClick: () => handleResilienceDriverClick(DefaultWidgetNames.default_project_pofh_indicator_card),
      values: pofhFactValues,
      indicator: 'biodiversity',
    }),
    useIndicatorSection({
      handleClick: () => handleResilienceDriverClick(DefaultWidgetNames.default_project_bgc_indicator_card),
      values: bgcFactValues,
      indicator: 'belowGroundCarbon',
    }),
    useIndicatorSection({
      handleClick: () => handleResilienceDriverClick(DefaultWidgetNames.default_project_social),
      values: socialFactValues,
      indicator: 'social',
    }),
  ];

  // Limit to 5 indicators for the card preview
  const indicatorsPreview = indicators
    .filter(
      (indicator) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (!isNullish((indicator.relatedData as any).currentFact) &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          !isNullish((indicator.relatedData as any).maxFact)) ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        !isNullish((indicator.relatedData as any).trendFact),
    )
    .slice(0, MAX_PREVIEW_INDICATORS);

  const shouldShowAllButton = indicatorsPreview.length > MAX_PREVIEW_INDICATORS;

  return {
    name: DefaultWidgetNames.default_resilience_drivers_card,
    type: WidgetType.a_stack,
    visible: !hasNoContent,
    relatedData: {
      className: 'flex-1',
    },
    widgets: [
      {
        name: 'indicator_card_dialog',
        type: WidgetType.o_c_indicator_card_dialog,
        visible: true,
        relatedData: {
          isOpen: isIndicatorDialogOpen,
          toggleIsOpen: toggleIsIndicatorDialogOpen,
          indicator: selectedIndicator,
          className: 'min-w-[60vw] min-h-[50vh]',
        },
      },
      {
        name: 'all_indicators_dialog',
        type: WidgetType.o_dialog_base,
        visible: true,
        relatedData: {
          open: isShowAllDialogOpen,
          onOpenChange: toggleIsShowAllDialogOpen,
        },
        widgets: [
          {
            name: 'all_indicators_dialog_content',
            type: WidgetType.o_dialog_content,
            visible: true,
            relatedData: {
              className: 'px-6 py-5',
            },
            widgets: [
              {
                name: 'all_indicators_dialog_content_title',
                type: WidgetType.a_text,
                visible: true,
                relatedData: {
                  className: 'typography-h3 text-text-primary px-1 py-4',
                  text: t('global.widget.resilienceDrivers.indicators'),
                },
              },
              {
                name: 'all_indicators_dialog_content_indicators_list',
                type: WidgetType.a_stack,
                visible: true,
                relatedData: {
                  direction: 'col',
                  spacing: 2,
                  className: 'max-h-[80vh] overflow-y-auto',
                },
                widgets: [...indicators],
              },
            ],
          },
        ],
      },
      {
        name: 'resilience_drivers_card_external_header',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          className: 'justify-between w-full py-4',
        },
        widgets: [
          {
            name: 'header_left_group',
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              direction: 'row',
              className: 'items-center typography-h3',
            },
            widgets: [
              {
                name: 'header_text',
                type: WidgetType.a_text,
                visible: true,
                relatedData: {
                  className: 'typography-h3 text-text-primary',
                  text: t('global.widget.resilienceDrivers.title'),
                },
              },
              {
                name: 'resilience_drivers_header_tooltip',
                type: WidgetType.m_tooltip,
                visible: true,
                widgets: [
                  {
                    name: 'resilience_drivers_header_tooltip_trigger',
                    type: WidgetType.m_tooltip_trigger,
                    visible: true,
                    widgets: [
                      {
                        name: 'resilience_drivers_header_tooltip_icon',
                        type: WidgetType.a_icon,
                        visible: true,
                        relatedData: {
                          icon: 'ri-information-line',
                          className: 'ml-1 text-active-54 typography-body1',
                        },
                      },
                    ],
                  },
                  {
                    name: 'resilience_drivers_header_tooltip_content',
                    type: WidgetType.m_tooltip_content,
                    visible: true,
                    widgets: [
                      {
                        name: 'resilience_drivers_header_tooltip_text',
                        type: WidgetType.a_text,
                        visible: true,
                        relatedData: {
                          i18nPath: 'global.widget.resilienceDrivers.tooltip.body',
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            name: 'show_all_button',
            type: WidgetType.a_button,
            visible: shouldShowAllButton,
            relatedData: {
              className: 'w-fit p-0 hover:bg-transparent underline hover:text-primary-hover',
              text: t('global.widget.resilienceDrivers.showAll'),
              variant: 'text',
              onClick: toggleIsShowAllDialogOpen,
            },
          },
        ],
      },
      {
        name: 'resilience_drivers_card',
        type: WidgetType.o_card,
        visible: true,
        relatedData: {
          styles: {
            root: 'border-none w-full flex-1',
            header: 'border-none typography-h3 px-0',
            body: 'border border-neutral-black-12 rounded-2xl flex-1 flex flex-col gap-4',
          },
        },
        widgets: [...indicatorsPreview],
      },
    ],
  };
};

type IndicatorSectionProps = {
  handleClick?: () => void;
  values: { [k: string]: PlotReportFactElement<unknown> | undefined };
  indicator: 'waterHoldingCapacity' | 'combinedCarbon' | 'biodiversity' | 'belowGroundCarbon' | 'social';
};

const useIndicatorSection = ({ handleClick, values, indicator }: IndicatorSectionProps) => {
  const { t } = useTranslation();

  return {
    name: `${indicator}_resilience_driver`,
    type: WidgetType.o_c_resilience_driver,
    visible: true,
    relatedData: {
      ...values,
      onClick: handleClick,
      title: t(`global.widget.resilienceDrivers.${indicator}.title`),
      tooltip: {
        title: t(`global.widget.resilienceDrivers.${indicator}.tooltip.title`),
        description: t(`global.widget.resilienceDrivers.${indicator}.tooltip.description`), // TODO: MVP-CW / MVP-5929 get text from figma when available
      },
    },
  };
};

const usePofhIndicatorSection = ({ handleClick, values, indicator }: IndicatorSectionProps) => {
  const { t } = useTranslation();

  const indicatorSection = useIndicatorSection({ handleClick, values, indicator });

  const maxThreshold = values?.maxFact?.value;

  if (
    isNullish(maxThreshold) ||
    typeof maxThreshold !== 'number' ||
    isNullish(values?.currentFact?.value) ||
    typeof values?.currentFact?.value !== 'number' ||
    values?.currentFact?.value < maxThreshold
  ) {
    return indicatorSection;
  }

  const progressBarTooltip =
    values.currentFact?.value === maxThreshold
      ? t('global.widget.resilienceDrivers.biodiversity.tooltip.descriptionIsThreshold')
      : t('global.widget.resilienceDrivers.biodiversity.tooltip.descriptionIsMoreThanThreshold');

  return {
    ...indicatorSection,
    relatedData: {
      ...indicatorSection.relatedData,
      progressBarTooltip,
    },
  };
};
